import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { X, Loader2 } from 'lucide-react';
import { API, formatErr, Field, inputCls, todayStr } from './shared';

const EditLogDialog = ({ log, item, onClose, onSaved, authHeader }) => {
  const isRestock = log.action === 'restock';
  const isConsume = log.action === 'consume';
  const initialQty = Math.abs(Number(log.quantity_change));
  const initialDate = (log.transaction_date || log.created_at || '').slice(0, 10);
  const [mode, setMode] = useState('set');
  const [qty, setQty] = useState(String(initialQty));
  const [adjustQty, setAdjustQty] = useState('');
  const [notes, setNotes] = useState(log.notes || '');
  const [carModel, setCarModel] = useState(log.car_model || '');
  const [txnDate, setTxnDate] = useState(initialDate);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const finalQty = useMemo(() => {
    const n = Number(adjustQty || 0);
    if (mode === 'add') return initialQty + n;
    if (mode === 'subtract') return initialQty - n;
    return Number(qty);
  }, [mode, adjustQty, qty, initialQty]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!Number.isFinite(finalQty) || finalQty <= 0) {
      setErr('Final quantity must be greater than zero.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/inventory-logs/${log.id}`,
        { quantity: finalQty, notes, transaction_date: txnDate, car_model: carModel },
        { headers: authHeader() }
      );
      onSaved();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const ModeBtn = ({ value, label, testid }) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={`flex-1 px-3 py-2 text-xs font-medium rounded-md border transition ${
        mode === value
          ? 'bg-teal-500 text-slate-950 border-teal-400'
          : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
      }`}
      data-testid={testid}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-teal-500/30 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold" data-testid="edit-log-title">
            Correct {isRestock ? 'Restock' : 'Consumption'} Entry
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="edit-log-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-200">
            Original entry: <strong>{initialQty} {item.unit}</strong> on {initialDate}. Current stock will adjust by the difference.
          </div>

          {/* Mode toggle */}
          <div>
            <span className="text-xs uppercase tracking-wider text-slate-400">How to correct</span>
            <div className="mt-1.5 flex gap-1.5">
              <ModeBtn value="set" label="Set new value" testid="edit-mode-set" />
              <ModeBtn value="add" label="Add" testid="edit-mode-add" />
              <ModeBtn value="subtract" label="Subtract" testid="edit-mode-subtract" />
            </div>
          </div>

          {mode === 'set' && (
            <Field label={`New quantity (${item.unit})`} required>
              <input
                type="number"
                required
                min="0.01"
                step="0.01"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className={inputCls}
                autoFocus
                data-testid="edit-log-qty"
              />
            </Field>
          )}

          {(mode === 'add' || mode === 'subtract') && (
            <Field label={`Quantity to ${mode} (${item.unit})`} required>
              <input
                type="number"
                required
                min="0.01"
                step="0.01"
                value={adjustQty}
                onChange={(e) => setAdjustQty(e.target.value)}
                className={inputCls}
                placeholder={`How much to ${mode}?`}
                autoFocus
                data-testid="edit-log-adjust-qty"
              />
              <p className="text-xs text-slate-500 mt-1">
                {initialQty} {mode === 'add' ? '+' : '−'} {adjustQty || 0} ={' '}
                <span className={`font-semibold ${finalQty <= 0 ? 'text-red-400' : 'text-teal-300'}`} data-testid="edit-log-preview">
                  {Number.isFinite(finalQty) ? finalQty : '—'} {item.unit}
                </span>
              </p>
            </Field>
          )}

          <Field label={isRestock ? 'Restock date' : 'Consumption date'}>
            <input
              type="date"
              value={txnDate}
              max={todayStr()}
              onChange={(e) => setTxnDate(e.target.value)}
              className={inputCls}
              data-testid="edit-log-date"
            />
          </Field>
          {isConsume && (
            <Field label="Car model">
              <input
                value={carModel}
                onChange={(e) => setCarModel(e.target.value)}
                className={inputCls}
                placeholder="e.g. Honda City"
                data-testid="edit-log-car-model"
              />
            </Field>
          )}
          <Field label="Notes">
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls}
              placeholder="Reason for correction (optional)"
              data-testid="edit-log-notes"
            />
          </Field>
          {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="edit-log-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg" data-testid="edit-log-cancel">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm bg-teal-500 hover:bg-teal-400 disabled:bg-teal-700 text-slate-950 font-semibold rounded-lg flex items-center gap-2"
              data-testid="edit-log-submit"
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              Save Correction
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLogDialog;
